/**
 * One recipient's grouped CSV orders, and the parcels those orders are being split across.
 *
 * Shown per recipient once "group orders by recipient" is on and that recipient turned out to have more
 * than one order. The parcel count is chosen here, and the orders can be dragged between the parcel
 * cards - which is why the plan is passed in rather than derived: an arrangement the customer makes by
 * hand has to survive, and CsvUpload owns it. See CombineCsvSmartSendMapResults.
 */
import { P2GBox, P2GText } from '@parcel2go/core-ui';
import { ConsignmentApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { consignmentParcelBound } from '../../lib/parcels/bounds';
import ConsignmentSummaryParcels from '../smartsend/panels/consignments/ConsignmentSummaryParcels';
import { ParcelCountPicker } from '../smartsend/panels/ParcelCountPicker';

export interface props {
  item: SmartSendItemApiModel;
  /**
   * The plan the cards show. Not always `item.Consignment` - at one parcel the item deliberately stores
   * no consignment, so a single-parcel plan is supplied purely for display.
   */
  consignment: ConsignmentApiModel;
  onParcelCountChange: (count: number) => void;
  onDragRowToParcel: (contentUID: string, targetParcelUID: string) => void;
  index: number;
}

export const CsvRecipientParcels: React.FC<props> = ({
  item,
  consignment,
  onParcelCountChange,
  onDragRowToParcel,
  index
}) => {
  const parcelCount = consignment?.Parcels?.length || 1;

  return (
    <P2GBox sx={{ textAlign: 'initial' }}>
      <P2GText>
        <strong>
          {index + 1})&nbsp;&nbsp;&nbsp;{item.Recipient.Name}, {item.Recipient.AddressLine1}{' '}
          {item.Recipient.AddressLine2}, {item.Recipient.Postcode} ({item.Recipient.CountryIsoCode})
        </strong>
      </P2GText>
      <P2GBox py={1}>
        <ParcelCountPicker
          // Only counts the allocator will actually honour. Offering more put options on screen that
          // produce an identical plan - the customer picks one and nothing visibly happens. Same bound the
          // item panel's picker uses, and read off the STORED consignment, since padding is one-way.
          availableParcels={consignmentParcelBound(item.MarketplaceOrderLines, item.Consignment)}
          onSelect={onParcelCountChange}
          // The plan is authoritative for how many parcels there are - the allocator may have clamped below
          // what was asked for, and highlighting the request would point at a parcel that does not exist.
          selected={parcelCount}
          helperText={null}
          justify="flex-start"
        />
      </P2GBox>

      <ConsignmentSummaryParcels
        consignment={consignment}
        currencyCode={SettingsCache.currencyCode}
        // Only draggable once there are cards to drag between - a one-parcel preview would otherwise show
        // handles that can never do anything.
        onDragRowToParcel={parcelCount > 1 ? onDragRowToParcel : undefined}
      />
    </P2GBox>
  );
};

export default CsvRecipientParcels;
